import { useFormContext } from 'react-hook-form';
import { DateIntervalFields, SelectField } from '@mews-commander/form';
import { LocalizedSubsectionTitle } from '@mews-commander/ui';
import { FormCheckboxField, FormSelectionCardGroupField, lt } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Container, Divider, Stack } from '@optimus-web/core';
import { GroupOption, Option } from '@mews-commander/types';
import { useCommanderSelector } from '@mews-commander/utils';
import { getDayOptions } from '@mews-commander/core/legacy-globalization';
import { SellLimitFieldNames } from '../fieldNames';
import { DeleteSellLimitFormModel } from '../../schemas';

interface Props {
    groupedRateOptions: GroupOption[];
    rateGroupOptions: Option[];
    resourceCategoryOptions: Option[];
}

export const DeleteSellLimitFormFields = ({ groupedRateOptions, rateGroupOptions, resourceCategoryOptions }: Props) => {
    const dayOptions = useCommanderSelector((state) => getDayOptions(state));
    const { trigger, watch, formState } = useFormContext<DeleteSellLimitFormModel>();

    const daysValue = watch(SellLimitFieldNames.DAYS_FIELD_NAME);
    const areAllDaysSelected = daysValue.length === 7;

    const { type: rateOptionType } = watch(SellLimitFieldNames.RATE_SELECT_OPTION_FIELD_NAME);
    const { type: spaceOptionType } = watch(SellLimitFieldNames.SPACE_SELECT_OPTION_FIELD_NAME);

    const rateSelectOptionContentMap = {
        exactRate: (
            <SelectField
                helper={lt('SellLimitsFormSelectRatesDeleteCaption')}
                key={SellLimitFieldNames.EXACT_RATE_IDS_FIELD_NAME}
                label={lt('SellLimitsFormSelectRatesLabel')}
                multiselect
                name={`${SellLimitFieldNames.RATE_SELECT_OPTION_FIELD_NAME}.${SellLimitFieldNames.EXACT_RATE_IDS_FIELD_NAME}`}
                options={groupedRateOptions}
                required
                shouldUnregister
                size="medium"
            />
        ),
        rateGroup: (
            <SelectField
                helper={lt('SellLimitsFormSelectRateGroupsDeleteCaption')}
                key={SellLimitFieldNames.RATE_GROUP_IDS_FIELD_NAME}
                label={lt('SelectRateGroupPlural')}
                multiselect
                name={`${SellLimitFieldNames.RATE_SELECT_OPTION_FIELD_NAME}.${SellLimitFieldNames.RATE_GROUP_IDS_FIELD_NAME}`}
                options={rateGroupOptions}
                required
                shouldUnregister
                size="medium"
            />
        ),
    };

    const spaceSelectOptionContentMap = {
        allSpaces: (
            <FormCheckboxField
                name={`${SellLimitFieldNames.SPACE_SELECT_OPTION_FIELD_NAME}.${SellLimitFieldNames.CLEAR_ALL_RESOURCE_CATEGORIES}`}
            >
                {lt('SellLimitsDeleteAllLimitsLabel')}
            </FormCheckboxField>
        ),
        resourceCategory: (
            <SelectField
                helper={lt('SellLimitsFormSelectSpacesDeleteCaption')}
                label={lt('SelectSpaceCategoryPlural')}
                multiselect
                name={`${SellLimitFieldNames.SPACE_SELECT_OPTION_FIELD_NAME}.${SellLimitFieldNames.RESOURCE_CATEGORY_IDS_FIELD_NAME}`}
                options={resourceCategoryOptions}
                required
                size="medium"
            />
        ),
    };

    return (
        <Stack vertical spacing="300">
            <Stack vertical spacing="100">
                <LocalizedSubsectionTitle textKey={TextKey.SellLimitsFormDeleteSectionTitle} />
            </Stack>
            <DateIntervalFields
                inline
                mode="utc"
                endDateProps={{
                    name: SellLimitFieldNames.LIMIT_END_FIELD_NAME,
                    label: lt('DeleteUntil'),
                    required: !areAllDaysSelected,
                }}
                size="medium"
                startDateProps={{
                    name: SellLimitFieldNames.LIMIT_START_FIELD_NAME,
                    label: lt('DeleteFrom'),
                    required: !areAllDaysSelected,
                }}
            />
            <SelectField
                helper={lt('SellLimitsFormDaysDeleteCaption')}
                label={lt('OnDays')}
                multiselect
                name={SellLimitFieldNames.DAYS_FIELD_NAME}
                options={dayOptions}
                required
                size="medium"
                onChange={async () => {
                    if (formState.isSubmitted) {
                        await trigger([
                            SellLimitFieldNames.LIMIT_START_FIELD_NAME,
                            SellLimitFieldNames.LIMIT_END_FIELD_NAME,
                        ]);
                    }
                }}
            />

            <Container padding={['100', '0']}>
                <Divider />
            </Container>

            <LocalizedSubsectionTitle textKey={TextKey.SellLimitsRateSelectOptionDeleteTitle} />
            <FormSelectionCardGroupField
                name={`${SellLimitFieldNames.RATE_SELECT_OPTION_FIELD_NAME}.type`}
                options={rateSelectOptions}
                type="radio"
            />
            {rateSelectOptionContentMap[rateOptionType]}
            <Container padding={['100', '0']}>
                <Divider />
            </Container>

            <LocalizedSubsectionTitle textKey={TextKey.SellLimitsSpaceSelectOptionDeleteTitle} />
            <FormSelectionCardGroupField
                type="radio"
                options={spaceSelectOptions}
                name={`${SellLimitFieldNames.SPACE_SELECT_OPTION_FIELD_NAME}.type`}
            />
            {spaceSelectOptionContentMap[spaceOptionType]}
        </Stack>
    );
};

const rateSelectOptions = [
    {
        description: lt('SellLimitsExactRateOptionDeleteLabel'),
        label: lt('ExactRate'),
        name: 'exactRate',
        value: 'exactRate',
    },
    {
        description: lt('SellLimitsRateGroupOptionDeleteLabel'),
        label: lt('RateGroup'),
        name: 'rateGroup',
        value: 'rateGroup',
    },
];
const spaceSelectOptions = [
    {
        description: lt('SellLimitsAllSpacesOptionDeleteLabel'),
        label: lt('AllSpaces'),
        name: 'allSpaces',
        value: 'allSpaces',
    },
    {
        description: lt('SellLimitsSpaceCategoryOptionDeleteLabel'),
        label: lt('SpaceCategory'),
        name: 'resourceCategory',
        value: 'resourceCategory',
    },
];
